import { useState, useEffect, useCallback } from "react";
import {
  Brain, Shield, ShieldAlert, Ban, Tag, Plus, X, Save,
  Loader2, BarChart3, CheckCircle, AlertTriangle, Clock,
  FileText, ArrowRight, Zap, Eye, Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SmartParsingSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState({});
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [activeSection, setActiveSection] = useState("settings");

  // Input states for lists
  const [senderInput, setSenderInput] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [identifierInput, setIdentifierInput] = useState("");

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/email/smart-parsing-settings`, { credentials: "include" });
      if (res.ok) setSettings(await res.json());
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await fetch(`${API}/api/email/parsing-logs?limit=30`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setLogStats(data.stats || {});
      }
    } catch { console.error("Failed to load parsing logs"); }
    finally { setLoadingLogs(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { if (activeSection === "logs") fetchLogs(); }, [activeSection, fetchLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/email/smart-parsing-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings)
      });
      if (res.ok) toast.success("Smart parsing settings saved");
      else toast.error("Failed to save settings");
    } catch { toast.error("Error saving settings"); }
    finally { setSaving(false); }
  };

  const addToList = (key, input, setInput) => {
    if (!input.trim()) return;
    setSettings(p => ({ ...p, [key]: [...(p[key] || []), input.trim()] }));
    setInput("");
  };

  const removeFromList = (key, idx) => {
    setSettings(p => ({ ...p, [key]: (p[key] || []).filter((_, i) => i !== idx) }));
  };

  if (loading || !settings) return (
    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
  );

  return (
    <div className="space-y-5" data-testid="smart-parsing-settings">
      {/* Section Tabs */}
      <div className="flex gap-0 border-b border-slate-200 dark:border-slate-700">
        {[
          { key: "settings", label: "Settings", icon: Zap },
          { key: "logs", label: "Parsing Logs", icon: FileText },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveSection(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeSection === tab.key
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`parsing-tab-${tab.key}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {activeSection === "settings" ? (
        <div className="space-y-5">
          {/* How it works */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/10 dark:to-blue-900/10 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-400 mb-1">Smart Email Parsing Engine</p>
                <p className="text-xs text-indigo-700/80 dark:text-indigo-400/80">
                  Automatically detects enquiry emails by analyzing content patterns — extracts Name, Phone, Email, Message, City &amp; Company.
                  Uses confidence scoring to flag uncertain extractions for manual review. No keyword dependency needed.
                </p>
              </div>
            </div>
          </div>

          {/* Core Settings */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" /> Core Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Enable Name Parsing from Email Body</p>
                  <p className="text-xs text-slate-500">Automatically extract lead name from email body fields (Name, Full Name, Student Name, etc.)</p>
                </div>
                <Switch checked={settings.enable_name_parsing ?? true}
                  onCheckedChange={c => setSettings(p => ({ ...p, enable_name_parsing: c }))}
                  data-testid="toggle-name-parsing" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Auto-create leads</p>
                  <p className="text-xs text-slate-500">Automatically create leads when email is detected as enquiry</p>
                </div>
                <Switch checked={settings.auto_create_lead}
                  onCheckedChange={c => setSettings(p => ({ ...p, auto_create_lead: c }))}
                  data-testid="toggle-auto-create" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Manual approval mode</p>
                  <p className="text-xs text-slate-500">Require admin approval before creating lead from email</p>
                </div>
                <Switch checked={settings.manual_approval_mode}
                  onCheckedChange={c => setSettings(p => ({ ...p, manual_approval_mode: c }))}
                  data-testid="toggle-manual-mode" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Minimum Confidence Score</Label>
                <p className="text-xs text-slate-500 mb-2">Only create leads with confidence score above this threshold (0-100)</p>
                <Input type="number" min={0} max={100} value={settings.min_confidence_score}
                  onChange={e => setSettings(p => ({ ...p, min_confidence_score: parseInt(e.target.value) || 0 }))}
                  className="w-32 h-9" data-testid="min-confidence-input" />
              </div>
            </CardContent>
          </Card>

          {/* Blocked Senders */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Ban className="w-4 h-4 text-red-500" /> Blocked Senders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input value={senderInput} onChange={e => setSenderInput(e.target.value)}
                  placeholder="e.g. noreply@company.com" className="flex-1 h-9"
                  onKeyDown={e => { if (e.key === "Enter") addToList("blocked_senders", senderInput, setSenderInput); }}
                  data-testid="blocked-sender-input" />
                <Button size="sm" variant="outline" onClick={() => addToList("blocked_senders", senderInput, setSenderInput)}
                  data-testid="add-blocked-sender"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(settings.blocked_senders || []).map((s, i) => (
                  <Badge key={i} variant="secondary" className="gap-1 pr-1 text-xs bg-red-50 text-red-700 border-red-200">
                    {s}
                    <button onClick={() => removeFromList("blocked_senders", i)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
                {(!settings.blocked_senders || settings.blocked_senders.length === 0) && (
                  <p className="text-xs text-slate-400">No blocked senders</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Blocked Keywords */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Filter className="w-4 h-4 text-orange-500" /> Blocked Keywords
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input value={keywordInput} onChange={e => setKeywordInput(e.target.value)}
                  placeholder="e.g. unsubscribe" className="flex-1 h-9"
                  onKeyDown={e => { if (e.key === "Enter") addToList("blocked_keywords", keywordInput, setKeywordInput); }}
                  data-testid="blocked-keyword-input" />
                <Button size="sm" variant="outline" onClick={() => addToList("blocked_keywords", keywordInput, setKeywordInput)}
                  data-testid="add-blocked-keyword"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(settings.blocked_keywords || []).map((k, i) => (
                  <Badge key={i} variant="secondary" className="gap-1 pr-1 text-xs bg-orange-50 text-orange-700 border-orange-200">
                    {k}
                    <button onClick={() => removeFromList("blocked_keywords", i)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
                {(!settings.blocked_keywords || settings.blocked_keywords.length === 0) && (
                  <p className="text-xs text-slate-400">No blocked keywords — built-in spam filter active</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Custom Field Identifiers */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Tag className="w-4 h-4 text-indigo-500" /> Custom Field Identifiers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">
                Add custom labels the parser should look for in email body (e.g., "Course Name:", "Property Type:")
              </p>
              <div className="flex gap-2 mb-3">
                <Input value={identifierInput} onChange={e => setIdentifierInput(e.target.value)}
                  placeholder="e.g. Course Name" className="flex-1 h-9"
                  onKeyDown={e => { if (e.key === "Enter") addToList("custom_field_identifiers", identifierInput, setIdentifierInput); }}
                  data-testid="custom-identifier-input" />
                <Button size="sm" variant="outline" onClick={() => addToList("custom_field_identifiers", identifierInput, setIdentifierInput)}
                  data-testid="add-custom-identifier"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(settings.custom_field_identifiers || []).map((id, i) => (
                  <Badge key={i} variant="secondary" className="gap-1 pr-1 text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                    {id}
                    <button onClick={() => removeFromList("custom_field_identifiers", i)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
                {(!settings.custom_field_identifiers || settings.custom_field_identifiers.length === 0) && (
                  <p className="text-xs text-slate-400">No custom identifiers — using default parsing patterns</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button onClick={handleSave} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="save-parsing-settings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Smart Parsing Settings
          </Button>
        </div>
      ) : (
        /* Parsing Logs Section */
        <div className="space-y-4" data-testid="parsing-logs-section">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total Parsed" value={logStats.total || 0} icon={FileText} color="slate" />
            <StatCard label="High Confidence" value={logStats.high_confidence || 0} icon={CheckCircle} color="green" />
            <StatCard label="Medium" value={logStats.medium_confidence || 0} icon={Clock} color="amber" />
            <StatCard label="Low / Review" value={logStats.low_confidence || 0} icon={AlertTriangle} color="red" />
          </div>

          {/* Log List */}
          {loadingLogs ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-xl border-slate-200">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm text-slate-500">No parsing logs yet</p>
              <p className="text-xs text-slate-400 mt-1">Logs will appear after emails are synced</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map(log => (
                <div key={log.log_id} className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700" data-testid={`parsing-log-${log.log_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{log.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{log.sender_email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <ConfidenceBadge score={log.parsing_score} label={log.confidence_label} />
                      <StatusBadge status={log.status} />
                    </div>
                  </div>
                  {log.extracted_fields && log.extracted_fields.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {log.extracted_fields.map(f => (
                        <Badge key={f} variant="outline" className="text-[10px] h-4 px-1.5 capitalize">{f}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colors = {
    slate: "bg-slate-50 text-slate-700",
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <div className={`p-3 rounded-lg text-center ${colors[color] || colors.slate}`}>
      <Icon className="w-5 h-5 mx-auto mb-1 opacity-60" />
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</p>
    </div>
  );
}

function ConfidenceBadge({ score, label }) {
  const cls = score >= 90 ? "bg-green-100 text-green-700 border-green-200"
    : score >= 70 ? "bg-amber-100 text-amber-700 border-amber-200"
    : "bg-red-100 text-red-700 border-red-200";
  return (
    <Badge variant="outline" className={`text-[10px] h-5 ${cls}`}>
      {score}% {label === "low" && "Review"}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const map = {
    lead_created: { label: "Created", cls: "bg-green-100 text-green-700" },
    duplicate_updated: { label: "Duplicate", cls: "bg-blue-100 text-blue-700" },
    pending_review: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
  };
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <Badge className={`text-[10px] h-5 ${s.cls}`}>{s.label}</Badge>;
}
